import { SupportedDateFormat } from '@gaia-family/ds'
import {
  gbGeneralConfig,
  SupportedUtsLocales,
  usGeneralConfig,
} from '@gaia-family/i18n'
import { HeightUnit, money, WeightUnit } from '@gaia-family/lib'
import { CurrencyString } from '@gaia-family/lib/src/money'
import {
  ConsultationType,
  Country,
  EmploymentStatus,
  HealthInsuranceCoverage,
  IvfProcedure,
} from '@graphql'
import { useRouter } from 'next/router'

// for URL - lowercased for SEO and UX
export enum SupportedUrlLocales {
  enGB = 'en-gb',
  enUS = 'en-us',
}

export const CountriesToLocale: Record<Country, SupportedUrlLocales> = {
  [Country.Gb]: SupportedUrlLocales.enGB,
  [Country.Us]: SupportedUrlLocales.enUS,
}

export const DEFAULT_LOCALE = SupportedUtsLocales.enGB

/**
 * A single source of truth for localised presentation concerns. We have
 * been relying on a lot of client-side conditional checks on country
 * and or locale to:
 *
 * 1. Generate options for dropdowns and other form elements
 * 2. Conditionally render strings in copy
 * 3. Conditionally render components
 *
 * This was confusing and led to many sources of truth, and lots of
 * localised product rules scattered through the FE codebase. The goal
 * of this is to unify all of these presentation concerns, excluding
 * actual react components, into a single config file. This also affords
 * us the advantage of having a light interface that can be leveraged to
 * move this config to the backend.
 */
type wwwI18nConfig = {
  urlLocale: SupportedUrlLocales
  locale: SupportedUtsLocales
  formatted: {
    date: SupportedDateFormat
  }
  country: Country
  units: {
    weight: {
      default: WeightUnit
      options: WeightUnit[]
    }
    height: {
      default: HeightUnit
    }
  }
  currency: {
    dinero: money.currencies.Currency<number>
    string: CurrencyString
  }
  clinicOptions: {
    canChangeClinic: boolean
    canNotHaveAClinicInMind: boolean
    canSpecifyUnlistedClinic: boolean
    mustSpecifyReferredClinic: boolean
  }
  treatmentProfileOptions: {
    explicitProcedureOptions: IvfProcedure[]
    previousProcedureOptions: IvfProcedure[]
    previousConsultationsOptions: ConsultationType[]
  }
  healthInsuranceCoverageOptions: Array<{
    name: string
    value: HealthInsuranceCoverage
  }>
  affordabilityOptions: {
    employmentOptions: {
      name: string
      value: EmploymentStatus
    }[]
  }
  links: {
    calendly: {
      highPriority: string
      standardPriority: string
    }
  }
}

const wwwGbConfig: wwwI18nConfig = {
  urlLocale: SupportedUrlLocales.enGB,
  locale: gbGeneralConfig.locale,
  formatted: {
    date: 'DD-MM-YYYY',
  },
  country: Country.Gb,
  units: {
    weight: {
      default: WeightUnit.Kilograms,
      options: [WeightUnit.Kilograms, WeightUnit.PoundsStones],
    },
    height: {
      default: HeightUnit.Centimetres,
    },
  },
  currency: {
    dinero: money[gbGeneralConfig.currency],
    string: CurrencyString.Pounds,
  },
  clinicOptions: {
    canChangeClinic: true,
    canNotHaveAClinicInMind: true,
    canSpecifyUnlistedClinic: false,
    mustSpecifyReferredClinic: true,
  },
  treatmentProfileOptions: {
    explicitProcedureOptions: [
      IvfProcedure.Icsi,
      IvfProcedure.FreezeAll,
      IvfProcedure.None,
    ],
    previousProcedureOptions: [
      IvfProcedure.EggDonation,
      IvfProcedure.Icsi,
      IvfProcedure.SpermDonation,
      IvfProcedure.Surrogate,
      IvfProcedure.FreezeAll,
      IvfProcedure.Pgd,
      IvfProcedure.None,
    ],
    previousConsultationsOptions: [
      ConsultationType.Gp,
      ConsultationType.NhsHospital,
      ConsultationType.PrivateFertilityClinic,
      ConsultationType.None,
      ConsultationType.Other,
    ],
  },
  healthInsuranceCoverageOptions: [],
  affordabilityOptions: {
    employmentOptions: [
      {
        value: EmploymentStatus.FullTimeEmployed,
        name: 'Full time employed',
      },
      {
        value: EmploymentStatus.PartTimeEmployed,
        name: 'Part time employed',
      },
      {
        value: EmploymentStatus.TemporarilyEmployed,
        name: 'Temporarily employed',
      },
      {
        value: EmploymentStatus.SelfEmployed,
        name: 'Self employed',
      },
      {
        value: EmploymentStatus.Unemployed,
        name: 'Unemployed',
      },
      { value: EmploymentStatus.Homemaker, name: 'Homemaker' },
      {
        value: EmploymentStatus.Student,
        name: 'Student',
      },
      {
        value: EmploymentStatus.Pension,
        name: 'Retired',
      },
      {
        value: EmploymentStatus.Other,
        name: 'Other',
      },
    ],
  },
  links: {
    calendly: {
      standardPriority: 'https://calendly.com/gaiamembersupport/your-quote-2',
      highPriority: 'https://calendly.com/gaiamembersupport/your-quote-1',
    },
  },
}

const wwwUsConfig: wwwI18nConfig = {
  urlLocale: SupportedUrlLocales.enUS,
  locale: usGeneralConfig.locale,
  formatted: {
    date: 'MM-DD-YYYY',
  },
  country: Country.Us,
  units: {
    weight: {
      default: WeightUnit.Pounds,
      options: [WeightUnit.Pounds, WeightUnit.Kilograms],
    },
    height: {
      default: HeightUnit.Inches,
    },
  },
  currency: {
    dinero: money[usGeneralConfig.currency],
    string: CurrencyString.Dollars,
  },
  clinicOptions: {
    canChangeClinic: false,
    canNotHaveAClinicInMind: false,
    canSpecifyUnlistedClinic: true,
    mustSpecifyReferredClinic: false,
  },
  treatmentProfileOptions: {
    explicitProcedureOptions: [
      IvfProcedure.Icsi,
      IvfProcedure.FreezeAll,
      IvfProcedure.PgtA,
      IvfProcedure.PgtM,
      IvfProcedure.None,
    ],
    previousProcedureOptions: [
      IvfProcedure.EggDonation,
      IvfProcedure.Icsi,
      IvfProcedure.SpermDonation,
      IvfProcedure.Surrogate,
      IvfProcedure.FreezeAll,
      IvfProcedure.PgtA,
      IvfProcedure.PgtM,
      IvfProcedure.None,
    ],
    previousConsultationsOptions: [
      ConsultationType.Obgyn,
      ConsultationType.PrivateFertilityClinic,
      ConsultationType.None,
      ConsultationType.Other,
    ],
  },
  healthInsuranceCoverageOptions: [
    {
      value: HealthInsuranceCoverage.DiagnosticTesting,
      name: 'Diagnostic testing',
    },
    {
      value: HealthInsuranceCoverage.Medication,
      name: 'Medication',
    },
    {
      value: HealthInsuranceCoverage.IvfTreatment,
      name: 'IVF treatment',
    },
    {
      value: HealthInsuranceCoverage.OtherFertilityTreatment,
      name: 'Other fertility treatment',
    },
    {
      value: HealthInsuranceCoverage.DontKnow,
      name: "I don't know",
    },
    {
      value: HealthInsuranceCoverage.None,
      name: 'None of the above',
    },
  ],
  affordabilityOptions: {
    employmentOptions: [
      {
        value: EmploymentStatus.FullTimeEmployed,
        name: 'Employed full-time',
      },
      {
        value: EmploymentStatus.PartTimeEmployed,
        name: 'Employed part-time',
      },
      {
        value: EmploymentStatus.Unemployed,
        name: 'Unemployed/homemaker',
      },
      {
        value: EmploymentStatus.Student,
        name: 'Full-time student',
      },
    ],
  },
  links: {
    calendly: {
      standardPriority: 'https://calendly.com/gaiamembersupport/gaia-call-us',
      highPriority: 'https://calendly.com/gaiamembersupport/gaia-call-us',
    },
  },
}

export const useLocalisedConfig = (): wwwI18nConfig => {
  const { locale } = useRouter()

  switch (locale) {
    case SupportedUrlLocales.enUS: {
      return wwwUsConfig
    }
    default: {
      return wwwGbConfig
    }
  }
}
